import { useState, useEffect, useCallback } from 'react'
import { Dialog } from '../dialog'

export interface StepDialogStep {
  content: React.ReactNode
}

interface StepDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger?: React.ReactNode
  title: string
  steps: StepDialogStep[]
  activeStep: number
}

type Phase = 'enter' | 'exit'

export const StepDialog = ({
  open,
  onOpenChange,
  trigger,
  title,
  steps,
  activeStep,
}: StepDialogProps) => {
  const [displayedStep, setDisplayedStep] = useState(activeStep)
  const [phase, setPhase] = useState<Phase>('enter')

  useEffect(() => {
    if (activeStep !== displayedStep) setPhase('exit')
  }, [activeStep, displayedStep])

  const handleAnimationEnd = () => {
    if (phase === 'exit') {
      setDisplayedStep(activeStep)
      setPhase('enter')
    }
  }

  const handleOpenChange = useCallback(
    (next: boolean) => {
      onOpenChange(next)
      if (!next) {
        setDisplayedStep(0)
        setPhase('enter')
      }
    },
    [onOpenChange]
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Dialog.Content onDismiss={() => handleOpenChange(false)}>
        <Dialog.Title>{title}</Dialog.Title>
        <div
          className={
            phase === 'exit'
              ? 'animate-out fade-out fill-mode-forwards duration-150'
              : 'animate-in fade-in fill-mode-forwards duration-200'
          }
          onAnimationEnd={handleAnimationEnd}
        >
          {steps[displayedStep]?.content}
        </div>
      </Dialog.Content>
    </Dialog>
  )
}
